/**
 * Shared response processing utilities for frontend components
 * This ensures consistent temporal context processing across web and Line OA channels
 */

// Temporal keywords for detection (matching backend implementation)
const temporalKeywords = [
  // English keywords
  'today', 'yesterday', 'tomorrow', 'this week', 'next week', 'last week',
  'this month', 'next month', 'last month', 'recent', 'latest', 'new',
  'due', 'overdue', 'completed', 'created', 'updated', 'schedule',
  'meeting', 'call', 'follow-up', 'deadline', 'timeline',
  // Thai keywords
  'วันนี้', 'เมื่อวาน', 'พรุ่งนี้', 'สัปดาห์นี้', 'สัปดาห์หน้า', 'สัปดาห์ที่แล้ว',
  'เดือนนี้', 'เดือนหน้า', 'เดือนที่แล้ว', 'ปีนี้', 'ปีหน้า', 'ปีที่แล้ว',
  'ล่าสุด', 'ใหม่', 'เร็วๆนี้', 'ครบกำหนด', 'เกินกำหนด', 'เสร็จแล้ว',
  'สร้างเมื่อ', 'อัปเดตเมื่อ', 'ตารางเวลา', 'ประชุม', 'โทร', 'ติดตาม',
  'กำหนดส่ง', 'ไทม์ไลน์', 'ครั้งต่อไป', 'ครั้งหน้า', 'ครั้งที่แล้ว'
];

// Date patterns for detection (matching backend implementation)
const datePatterns = [
  /\b(today|yesterday|tomorrow)\b/i,
  /\b(this|next|last)\s+(week|month|year)\b/i,
  /\b(due|overdue|completed|created|updated)\b/i,
  /\b(recent|latest|new)\b/i,
  /\b\d{1,2}\/\d{1,2}\/\d{2,4}\b/, // MM/DD/YYYY or DD/MM/YYYY
  /\b\d{4}-\d{2}-\d{2}\b/, // YYYY-MM-DD
  /\b(january|february|march|april|may|june|july|august|september|october|november|december)\b/i,
  /\b(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\b/i,
  // Thai temporal patterns
  /\b(วันนี้|เมื่อวาน|พรุ่งนี้)\b/i,
  /\b(สัปดาห์นี้|สัปดาห์หน้า|สัปดาห์ที่แล้ว)\b/i,
  /\b(เดือนนี้|เดือนหน้า|เดือนที่แล้ว)\b/i,
  /\b(ปีนี้|ปีหน้า|ปีที่แล้ว)\b/i,
  /\b(ครั้งต่อไป|ครั้งหน้า|ครั้งที่แล้ว)\b/i
];

/**
 * Detect if a query contains temporal keywords
 */
export const isTemporalQuery = (query) => {
  if (!query || typeof query !== 'string') return false;
  
  const hasTemporalKeywords = temporalKeywords.some(keyword => 
    query.toLowerCase().includes(keyword.toLowerCase())
  );
  
  const hasDatePatterns = datePatterns.some(pattern => pattern.test(query));
  
  console.log('🔍 Temporal query detection:', {
    query: query.substring(0, 100),
    hasTemporalKeywords,
    hasDatePatterns,
    isTemporal: hasTemporalKeywords || hasDatePatterns
  });
  
  return hasTemporalKeywords || hasDatePatterns;
};

/**
 * Extract temporal keywords from a query
 */
export const extractTemporalKeywords = (query) => {
  if (!query || typeof query !== 'string') return [];
  
  const found = [];
  temporalKeywords.forEach(keyword => {
    if (query.toLowerCase().includes(keyword.toLowerCase())) {
      found.push(keyword);
    }
  });
  
  return found;
};

/**
 * Enhance query with temporal context information
 */
export const enhanceQueryWithTemporalContext = (query) => {
  if (!query || typeof query !== 'string') return query;
  
  const now = new Date();
  const today = now.toLocaleDateString();
  const thaiToday = now.toLocaleDateString('th-TH', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  const dayOfWeek = now.toLocaleDateString('en-US', { weekday: 'long' });
  
  const isTemporal = isTemporalQuery(query);
  
  console.log('🔍 Temporal context enhancement:', {
    originalQuery: query.substring(0, 100),
    isTemporal,
    foundKeywords: extractTemporalKeywords(query)
  });
  
  if (isTemporal) {
    return `${query}\n\nTemporal context: Today is ${today} (${dayOfWeek}) / ${thaiToday}. Please provide information relative to this current date.`;
  }
  
  return query;
};

/**
 * Detect date-related queries
 */
export const isDateRelatedQuery = (query) => {
  if (!query || typeof query !== 'string') return false;
  
  const isDateRelated = datePatterns.some(pattern => pattern.test(query));
  
  console.log('🔍 Date-related query detection:', {
    query: query.substring(0, 100),
    isDateRelated,
    matchedPatterns: datePatterns.filter(pattern => pattern.test(query)).map(p => p.source)
  });
  
  return isDateRelated;
};

/**
 * Safely extract first JSON object from a string
 */
export const extractFirstJson = (text) => {
  try {
    return JSON.parse(text);
  } catch (e) {
    try {
      const start = text.indexOf('{');
      const end = text.lastIndexOf('}');
      if (start !== -1 && end !== -1 && end > start) {
        const slice = text.substring(start, end + 1);
        return JSON.parse(slice);
      }
    } catch (_) {}
  }
  return null;
};

/**
 * Normalize AI intent output to one of the allowed values
 */
export const normalizeIntentType = (intentObj, userText) => {
  console.log('🔍 normalizeIntentType called with intentObj:', intentObj, 'userText:', userText);
  const allowed = ['Question', 'Create Note', 'Create Activity'];
  const raw = (intentObj && (intentObj.type || intentObj.Type || intentObj.intent || intentObj.Intent)) || '';
  const t = String(raw).toLowerCase().trim();
  console.log('🔍 Intent analysis - Raw:', raw, 'Normalized:', t, 'intentObj is null:', !intentObj);
  
  if (t === 'question' || /\b(question|ask|inquiry)\b/.test(t)) return 'Question';
  if (t === 'create note' || /\b(note|record|save)\b/.test(t)) return 'Create Note';
  if (t === 'create activity' || /\b(activity|schedule|follow[- ]?up|task|meeting|call)\b/.test(t)) return 'Create Activity';
  
  // Heuristics fallback
  if (/\?\s*$/.test(userText) || /^(who|what|when|where|why|how)\b/i.test(String(userText).trim())) return 'Question';
  console.log('🔍 Falling back to Question due to no match');
  return 'Question';
};

/**
 * Check if intent is a question
 */
export const isQuestionIntent = (intentObj, text) => {
  const norm = normalizeIntentType(intentObj, text);
  console.log('🔍 isQuestionIntent - Normalized:', norm);
  return norm === 'Question';
};

/**
 * Parse markdown for display (for web interface)
 */
export const parseMarkdown = (text) => {
  if (!text) return text;
  
  let parsed = text;
  
  // Convert **bold** to <strong>bold</strong> (must come before single *)
  parsed = parsed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Convert *italic* to <em>italic</em> (avoid conflicts with bold)
  parsed = parsed.replace(/\*([^*\n]+?)\*/g, '<em>$1</em>');
  
  // Convert `code` to <code>code</code> with dark theme support
  parsed = parsed.replace(/`(.*?)`/g, '<code class="bg-gray-100 dark:bg-gray-600 px-1 py-0.5 rounded text-sm font-mono">$1</code>');
  
  // Handle numbered lists properly - preserve original numbering
  const numberedListRegex = /^[\s]*(\d+)\.\s+(.*)$/gm;
  let numberedListMatch;
  let numberedListItems = [];
  
  // Find all numbered list items and their positions
  while ((numberedListMatch = numberedListRegex.exec(parsed)) !== null) {
    numberedListItems.push({
      index: numberedListMatch.index,
      number: numberedListMatch[1],
      content: numberedListMatch[2],
      fullMatch: numberedListMatch[0]
    });
  }
  
  // Replace numbered lists with proper HTML structure
  if (numberedListItems.length > 0) {
    // Sort by index in reverse order to avoid index shifting
    numberedListItems.sort((a, b) => b.index - a.index);
    
    numberedListItems.forEach(item => {
      // Use the original number in the content to preserve numbering
      // Reduced margin and padding for more compact display
      const listItemHtml = `<li style="margin: 2px 0; padding: 0; margin-left: 16px; line-height: 1.4;"><strong>${item.number}.</strong> ${item.content}</li>`;
      parsed = parsed.substring(0, item.index) + listItemHtml + parsed.substring(item.index + item.fullMatch.length);
    });
  }
  
  // Convert bullet points (-, *, or existing •) to HTML lists
  // - If the content already starts with a bullet "•", strip it to avoid double bullets
  // Reduced margin and padding for more compact display
  parsed = parsed.replace(/^[\s]*[-*]\s+(?:•\s*)?(.*)$/gm, '<li style="margin: 2px 0; padding: 0; margin-left: 16px; line-height: 1.4;">• $1</li>');
  // - Lines that already begin with a bullet dot should also be treated as list items
  parsed = parsed.replace(/^[\s]*•\s+(.*)$/gm, '<li style="margin: 2px 0; padding: 0; margin-left: 16px; line-height: 1.4;">• $1</li>');
  
  // Convert ### Headers to h3
  parsed = parsed.replace(/^###\s+(.*)$/gm, '<h3 style="font-size: 1.1em; font-weight: bold; margin: 8px 0 4px 0;">$1</h3>');
  
  // Convert ## Headers to h2  
  parsed = parsed.replace(/^##\s+(.*)$/gm, '<h2 style="font-size: 1.2em; font-weight: bold; margin: 10px 0 6px 0;">$1</h2>');
  
  // Convert # Headers to h1
  parsed = parsed.replace(/^#\s+(.*)$/gm, '<h1 style="font-size: 1.3em; font-weight: bold; margin: 12px 0 8px 0;">$1</h1>');
  
  // Convert markdown links [text](url) to HTML links (must come before line break conversion)
  parsed = parsed.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline">$1</a>');
  
  // Final cleanup: collapse any accidental double bullets to a single one
  parsed = parsed.replace(/•\s*•\s*/g, '• ');

  // Convert line breaks to <br>
  parsed = parsed.replace(/\n/g, '<br>');
  
  return parsed;
};

export default {
  isTemporalQuery,
  extractTemporalKeywords,
  enhanceQueryWithTemporalContext,
  isDateRelatedQuery,
  extractFirstJson,
  normalizeIntentType,
  isQuestionIntent,
  parseMarkdown
};
